import { IcCopyPaste, IcEdit, IcPlus as IcPlus24, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { ButtonGroup, For, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { PilotSelectors } from '@redux/utm/utm.selectors';
import { useCreatePilotMutation, useDeletePilotMutation } from '@services/utm/pilot.api';
import { createErrorMessage } from '@utils/common.utils';
import { getPilotName } from '@utils/utm/pilot.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { RoleEnum } from '@/types/authent/roles.types';
import { Pilot } from '@/types/utm/pilot.types';

import { useUtmContext, UtmItemTypeEnum } from '../UtmManagement';

type PilotsActionsProps = {
  canCreatePilot: boolean;
  pilotsLength: number;
  onCreate: () => void;
};

function PilotsActions({ canCreatePilot, pilotsLength, onCreate }: Readonly<PilotsActionsProps>) {
  return (
    <>
      <Text color="neutral.500">
        <FormattedMessage id="components.utm.pilots.result" values={{ count: pilotsLength }} />
      </Text>
      {canCreatePilot && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

function PilotsHeader() {
  return (
    <>
      <Text width="270px" color="neutral.500">
        <FormattedMessage id="components.utm.pilots.name" />
      </Text>
      <Text width="240px" color="neutral.500">
        <FormattedMessage id="components.utm.pilots.contact" />
      </Text>
    </>
  );
}

export default function Pilots() {
  const { formatMessage } = useIntl();
  const pilots = useAppSelector(PilotSelectors.selectAllPilots);
  const [selectedPilot, setSelectedPilot] = useState<Pilot | null>(null);
  const [deletePilot, { isLoading }] = useDeletePilotMutation();
  const [createPilot] = useCreatePilotMutation();
  const [loadingCopy, setLoadingCopy] = useState<number | null>(null);
  const canEditPilot = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_PILOT));
  const canCreatePilot = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_PILOT));
  const canDeletePilot = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_PILOT));

  const { setEditedItem } = useUtmContext();

  return (
    <CockpitListLayout
      dialog={
        <CockpitDialog
          open={selectedPilot !== null}
          isLoading={isLoading}
          onCancel={() => setSelectedPilot(null)}
          onConfirm={() => {
            if (!selectedPilot) {
              return;
            }
            deletePilot(selectedPilot.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage({ id: 'components.utm.pilots.message.delete' }, { name: getPilotName(selectedPilot) }),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedPilot(null);
              })
              .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
          }}
        >
          <Text color="neutral.100" width="100%" whiteSpace="pre-line">
            <FormattedMessage
              id="components.utm.pilots.popup.descriptionDelete"
              values={{ name: getPilotName(selectedPilot) }}
            />
          </Text>
        </CockpitDialog>
      }
      actions={
        <PilotsActions
          canCreatePilot={canCreatePilot}
          pilotsLength={pilots.length}
          onCreate={() => setEditedItem({ type: UtmItemTypeEnum.PILOT, value: null })}
        />
      }
      header={pilots.length > 0 && <PilotsHeader />}
    >
      <For
        each={pilots}
        fallback={
          canCreatePilot ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedItem({ type: UtmItemTypeEnum.PILOT, value: null })}
            >
              <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.utm.pilots.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(pilot, index) => {
          const isPilotFromClearance = pilot.code.startsWith('clearance-');
          return (
            <CockpitRow index={index} listLength={pilots.length} key={index}>
              <Tooltip content={getPilotName(pilot)} openDelay={500}>
                <Text lineClamp={1} width="270px" fontWeight="medium" color="neutral.200">
                  {getPilotName(pilot)}
                </Text>
              </Tooltip>
              <Text width="240px">{pilot.phoneNumber}</Text>
              {!isPilotFromClearance && (
                <ButtonGroup marginLeft="auto" attached height="100%">
                  {canEditPilot && (
                    <IconButton
                      variant="transparent"
                      boxSize="56px"
                      aria-label="edit"
                      onClick={() => setEditedItem({ type: UtmItemTypeEnum.PILOT, value: pilot })}
                    >
                      <Icon as={IcEdit} boxSize="24px" />
                    </IconButton>
                  )}
                  {canCreatePilot && (
                    <IconButton
                      variant="transparent"
                      boxSize="56px"
                      aria-label="duplicate"
                      loading={loadingCopy === pilot.id}
                      onClick={() => {
                        setLoadingCopy(pilot.id);
                        createPilot({
                          ...pilot,
                          firstName: pilot.lastName ? pilot.firstName : `${pilot.firstName}-copy`,
                          lastName: pilot.lastName ? `${pilot.lastName}-copy` : null,
                          id: undefined,
                          code: uuidv4(),
                        })
                          .unwrap()
                          .then(() => {
                            setLoadingCopy(null);
                            createToast(
                              formatMessage(
                                { id: 'components.utm.pilots.message.copy' },
                                { name: getPilotName(pilot) },
                              ),
                              ToastStatusEnum.SUCCESS,
                            );
                          });
                      }}
                    >
                      <Icon as={IcCopyPaste} boxSize="24px" />
                    </IconButton>
                  )}
                  {canDeletePilot && (
                    <IconButton
                      variant="transparent"
                      boxSize="56px"
                      aria-label="delete"
                      onClick={() => setSelectedPilot(pilot)}
                    >
                      <Icon as={IcTrash} boxSize="24px" />
                    </IconButton>
                  )}
                </ButtonGroup>
              )}
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}
